/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from 'react';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { toYYYYMMDD } from '@/app/components/search/utils/misc';
import type { Dates } from '@/app/components/search/utils';

export const splitDateTime = (date: string) => {
  const dateParts = date.split('-');
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1;
  const day = parseInt(dateParts[2], 10);

  return { year, month, day };
};

export const getContainerEl = (
  e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>
): {
  hoverDate: string | null;
  shouldReturn: boolean;
  isStart: boolean;
  isEnd: boolean;
  containerEl: HTMLDivElement | null;
} => {
  if (canUseDOM) {
    const el = e?.target as HTMLDivElement;

    if (el && el.classList.contains('day')) {
      let containerEl: HTMLDivElement | null = null;
      if (el.getAttribute('data-date')) {
        containerEl = el as HTMLDivElement;
      } else if (el.parentNode) {
        if ((el.parentNode as HTMLDivElement).getAttribute('data-date')) {
          containerEl = el.parentNode as HTMLDivElement;
        } else {
          containerEl = el.parentNode.parentNode as HTMLDivElement;
        }
      }

      if (containerEl) {
        const hoverDate = containerEl.getAttribute('data-date');
        const shouldReturn =
          containerEl.classList.contains('day-empty') || containerEl.classList.contains('day-disabled');
        const isStart = containerEl.classList.contains('day-active-start');
        const isEnd = containerEl.classList.contains('day-active-end');
        if (shouldReturn)
          return { hoverDate: null, shouldReturn: false, isStart: false, isEnd: false, containerEl: null };

        return { hoverDate, shouldReturn, isStart, isEnd, containerEl };
      }
    }
  }

  return { hoverDate: null, shouldReturn: false, isStart: false, isEnd: false, containerEl: null };
};

export const convertDate = (date: string): number => new Date(date).setHours(0, 0, 0, 0);

export const getDaysRange = ({ startDate, endDate }: { startDate: string; endDate: string }): string[] => {
  const { year, month, day } = splitDateTime(startDate);
  const start = new Date(year, month, day, 0, 0, 0);
  const { year: yearEnd, month: monthEnd, day: dayEnd } = splitDateTime(endDate);
  const end = new Date(yearEnd, monthEnd, dayEnd, 0, 0, 0);
  const dates: any = [];
  let currentDate = start;

  while (currentDate <= end) {
    dates.push(toYYYYMMDD(currentDate));
    currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1, 0, 0, 0);
  }
  return dates;
};

export const isDateInsideRange = ({ bounds, dates }: { bounds?: Dates; dates?: Dates }) => {
  const startBoundDate = bounds?.departureDate?.date;
  const endBoundDate = bounds?.returnDate?.date;
  const startDateSelected = dates?.departureDate?.date;
  const endDateSelected = dates?.returnDate?.date;

  const hasDates = startBoundDate && endBoundDate;
  const hasNewDates = startDateSelected && endDateSelected;

  const range = hasDates ? getDaysRange({ startDate: startBoundDate, endDate: endBoundDate }) : [];
  return hasNewDates ? range.includes(startDateSelected) && range.includes(endDateSelected) : false;
};

export const DecorateDates = ({
  containerRef,
  startDate,
  endDate,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  startDate?: string;
  endDate?: string;
}): void => {
  if (containerRef.current && startDate) {
    if (endDate) {
      const daysRange = getDaysRange({ startDate, endDate });

      daysRange.forEach((item: string, i: number) => {
        if (containerRef.current) {
          const elHover = containerRef.current.querySelectorAll(`[data-date="${item}"]`)[0] as HTMLDivElement;
          if (elHover) {
            if (i === 0) {
              elHover.classList.add('day-active');
              elHover.classList.add('day-active-start');
            } else if (i === daysRange.length - 1) {
              elHover.classList.add('day-active');
              elHover.classList.add('day-active-end');
            } else {
              elHover.classList.add('day-active');
              elHover.classList.remove('day-active-start');
              elHover.classList.remove('day-active-end');
            }
          }
        }
      });
    } else {
      const elHover = containerRef.current.querySelectorAll(`[data-date="${startDate}"]`)[0] as HTMLDivElement;
      elHover.classList.add('day-active-exact');
    }
  }
};

export const resetHoverDates = (items: Element[]): void => {
  items.forEach((item) => {
    item.classList.remove('day-highlight-active');
    item.classList.remove('day-highlight-active-start');
    item.classList.remove('day-highlight-active-end');
  });
};

const enrichHoverDatesStyles = (items: Element[]) => {
  items.forEach((item, i) => {
    if (i === 0) {
      item.classList.add('day-highlight-active');
      item.classList.add('day-highlight-active-start');
    } else if (i === items.length - 1) {
      item.classList.add('day-highlight-active');
      item.classList.add('day-highlight-active-end');
    } else if (item.classList.contains('day-active')) {
      item.classList.add('day-highlight-active');
    } else {
      item.classList.add('day-highlight-active');
      item.classList.remove('day-highlight-active-start');
      item.classList.remove('day-highlight-active-end');
    }
  });
};

export const DecorateDatesHover = ({
  containerRef,
  startDate,
  endDate,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  startDate?: string | null;
  endDate?: string | null;
}): void => {
  if (containerRef.current) {
    const allItems = [...containerRef.current.querySelectorAll('[data-date]')];

    if (startDate && endDate) {
      const startHighlightDate = convertDate(startDate || '') > convertDate(endDate || '') ? endDate : startDate;
      const endHighlightDate = convertDate(startDate || '') < convertDate(endDate || '') ? endDate : startDate;
      const daysRange = getDaysRange({ startDate: startHighlightDate, endDate: endHighlightDate });

      if (allItems) {
        resetHoverDates(allItems);
      }

      const selectedItems = allItems.filter((item) => {
        const x = item.getAttribute('data-date');
        return x ? daysRange.includes(x) : false;
      });

      if (selectedItems) {
        enrichHoverDatesStyles(selectedItems);
      }
    } else {
      resetHoverDates(allItems);
    }
  }
};
